import React, { useState, useContext, useEffect, useRef }  from "react";

// Section for Utilities and Context
import './profileissues.css';
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";
import { getIssueIcon } from "../utils/issueIcons";

// Section for Components


// Local Functions
export default function ProfileIssuesFeed(props) {
    const { auth, isAdmin } = useContext(AuthContext);
    const [profileId, setProfileId] = useState(null);
    const [issues, setIssues] = useState([]);
    const [issueSearchTerm, setIssueSearchTerm] = useState("");
    const [highlightedIssueIndex, setHighlightedIssueIndex] = useState(-1);
    const [redundantIssue, setRedundantIssue] = useState(false);
    const [pendingIssues, setPendingIssues] = useState([]);
    const [confirmPayload, setConfirmPayload] = useState(null);
    const dropdownLength = 5;
    const dropdownRef = useRef(null);
    const canEdit = isAdmin || auth.user.user_id === props.user_id || (auth.user.contributor && props.user_id === null);

    // API IMPORT AND HANDLERS OF ISSUE AREA DATA
    useEffect(() => {
        const fetchIssueAreas = async () => {
            try {
                const response = await API.getIssueAreas();
                const existingNames = new Set([
                    ...props.issues.map(i => i.IssueArea.issue_name.toLowerCase()),
                    ...pendingIssues.map(i => i.issue_name.toLowerCase())
                ]);
                const filtered = response.data.filter(issue =>
                    issue.issue_name.toLowerCase().includes(issueSearchTerm.toLowerCase()) &&
                    !existingNames.has(issue.issue_name.toLowerCase())
                );
                setIssues(filtered.slice(0, 5));
            } catch (error) {
                console.error("Error fetching issue areas:", error);
            }
        };

        if (issueSearchTerm !== "") {
            fetchIssueAreas();
        } else {
            setIssues([]);
        }

        setProfileId(props.profile_id);
    }, [issueSearchTerm]);

    const sortedIssues = [...props.issues].sort((a, b) => {
        if (a.IssueArea.issue_name < b.IssueArea.issue_name) return -1;
        if (a.IssueArea.issue_name > b.IssueArea.issue_name) return 1;
        return 0;
    });

    const isDuplicate = (name) => {
        const lower = name.toLowerCase();
        return (
            props.issues.some(i => i.IssueArea.issue_name.toLowerCase() === lower) ||
            pendingIssues.some(i => i.issue_name.toLowerCase() === lower)
        );
    };

    const handleIssueInputChange = (e) => {
        const term = e.target.value;
        setIssueSearchTerm(term);
        setRedundantIssue(term !== "" && isDuplicate(term));
        setHighlightedIssueIndex(-1);
    };

    // Stage an issue as a chip — does not save to DB yet
    const stageIssue = (name, id) => {
        if (isDuplicate(name)) {
            setRedundantIssue(true);
            return;
        }
        setPendingIssues(prev => [...prev, { issue_name: name, issue_id: id || null }]);
        setIssueSearchTerm("");
        setHighlightedIssueIndex(-1);
        setRedundantIssue(false);
        setIssues([]);
    };

    const removePendingIssue = (index) => {
        setPendingIssues(prev => prev.filter((_, i) => i !== index));
    };

    // DROPDOWN HEIGHT
    const calculateDropdownHeight = () => `${35 * dropdownLength}px`;

    // HANDLER FOR NAVIGATING DROPDOWN MENU
    const handleIssueKeyDown = (e) => {
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIssueIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIssueIndex(prev => Math.min(prev + 1, issues.length - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIssueIndex !== -1 && issues[highlightedIssueIndex]) {
                // Stage the highlighted dropdown item
                stageIssue(issues[highlightedIssueIndex].issue_name, issues[highlightedIssueIndex].issue_id);
            } else if (issueSearchTerm.trim() !== "") {
                const name = issueSearchTerm.trim();
                if (!isDuplicate(name)) {
                    // Stage the typed text as new issue, then submit everything
                    const newPending = [...pendingIssues, { issue_name: name, issue_id: null }];
                    setPendingIssues(newPending);
                    setIssueSearchTerm("");
                    setIssues([]);
                    submitIssues(newPending);
                } else {
                    setRedundantIssue(true);
                }
            } else {
                // Input is empty — submit all staged issues
                submitIssues(pendingIssues);
            }
        }
    };

    // Save all pending issues to the database (called after any confirmation)
    const performSubmit = async (toSubmit) => {
        try {
            for (const issue of toSubmit) {
                const data = { profile_id: profileId };

                if (issue.issue_id) {
                    data.issue_id = issue.issue_id;
                } else {
                    await API.addIssueArea({ issue_name: issue.issue_name });
                    const fetchResponse = await API.getIssueAreas();
                    const found = fetchResponse.data.find(i => i.issue_name === issue.issue_name);
                    if (found) data.issue_id = found.issue_id;
                }

                await API.addUserIssue(data);
            }
            props.reRenderProfile();
            setPendingIssues([]);
            setConfirmPayload(null);
        } catch (error) {
            console.error("Server request failed:", error);
        }
    };

    // If any issues are new (not in DB), require confirmation before writing
    const submitIssues = (toSubmit) => {
        if (toSubmit.length === 0) return;
        const hasNew = toSubmit.some(i => !i.issue_id);
        if (hasNew) {
            setConfirmPayload(toSubmit);
        } else {
            performSubmit(toSubmit);
        }
    };

    const handleAddButtonClick = () => {
        if (issueSearchTerm.trim() !== "" && !redundantIssue) {
            const matchInDropdown = issues.find(i => i.issue_name.toLowerCase() === issueSearchTerm.toLowerCase());
            stageIssue(issueSearchTerm.trim(), matchInDropdown?.issue_id || null);
        } else if (pendingIssues.length > 0) {
            submitIssues(pendingIssues);
        }
    };

    // HANDLER FOR DELETING A SAVED ISSUE RECORD
    const handleDeleteUserIssue = async (id) => {
        try {
            await API.deleteUserIssue({ id });
            props.reRenderProfile();
        } catch (error) {
            console.error("Server request failed:", error);
        }
    };

    return (
        <>
            <h5 className="current-issues-header display-6 mb-3">Current Issue Areas</h5>

            {canEdit && (
                <>
                    <div className="input-group input-group-sm mb-3">
                        <input
                            type="text"
                            className="form-control rounded-end"
                            placeholder="Search or add an issue area..."
                            value={issueSearchTerm}
                            onChange={handleIssueInputChange}
                            onKeyDown={handleIssueKeyDown}
                        />
                        <span>
                            <i
                                className={`bi ${pendingIssues.length > 0 && !issueSearchTerm ? 'bi-check-circle' : 'bi-plus-circle'} add-issue-icon ms-2`}
                                onClick={handleAddButtonClick}
                            />
                        </span>
                    </div>
                    {issues.length > 0 && (
                        <select
                            className="form-select select-menu"
                            size="5"
                            style={{ maxHeight: calculateDropdownHeight(), overflowY: "auto" }}
                            ref={dropdownRef}
                        >
                            {issues.map((issue, index) => (
                                <option
                                    key={issue.issue_id}
                                    className={`dropdown-item add-job-option ${index === highlightedIssueIndex ? "active-option" : ""}`}
                                    onClick={() => stageIssue(issue.issue_name, issue.issue_id)}
                                >
                                    {issue.issue_name}
                                </option>
                            ))}
                        </select>
                    )}
                    {redundantIssue && (
                        <div className="redundant-issue-message mb-2 pt-0">You already cover this issue.</div>
                    )}
                    {confirmPayload && (
                        <div className="confirm-new-issue mb-2">
                            <div className="confirm-new-issue-msg">
                                <i className="bi bi-exclamation-triangle me-1" />
                                The following issue area{confirmPayload.filter(i => !i.issue_id).length > 1 ? 's are' : ' is'} not yet in the database and will be created:
                                <span className="confirm-new-names">
                                    {confirmPayload.filter(i => !i.issue_id).map(i => i.issue_name).join(', ')}
                                </span>
                            </div>
                            <div className="confirm-new-issue-actions">
                                <button className="confirm-btn confirm-btn-yes" onClick={() => performSubmit(confirmPayload)}>
                                    Yes, save
                                </button>
                                <button className="confirm-btn confirm-btn-no" onClick={() => {
                                        const newNames = new Set(confirmPayload.filter(i => !i.issue_id).map(i => i.issue_name.toLowerCase()));
                                        setPendingIssues(prev => prev.filter(i => !newNames.has(i.issue_name.toLowerCase())));
                                        setConfirmPayload(null);
                                    }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    {pendingIssues.length > 0 && !issueSearchTerm && !confirmPayload && (
                        <div className="pending-hint mb-1">
                            Press Enter or click ✓ to save {pendingIssues.length} pending issue{pendingIssues.length > 1 ? 's' : ''}
                        </div>
                    )}
                    {pendingIssues.length > 0 && (
                        <div className="pending-issues-chips mb-3">
                            {pendingIssues.map((issue, index) => (
                                <span key={index} className="issue-chip">
                                    {issue.issue_name}
                                    <i className="bi bi-x ms-1" onClick={() => removePendingIssue(index)} />
                                </span>
                            ))}
                        </div>
                    )}
                </>
            )}

            <div className="issue-area-cards">
                {props !== undefined && props.issues.length > 0 ? (
                    sortedIssues.map((issue) => (
                        <div key={issue.IssueArea.issue_id} className="issue-area-card">
                            <i className={`bi ${issue.IssueArea.issue_icon || getIssueIcon(issue.IssueArea.issue_name)} issue-area-card-icon`} />
                            <span className="issue-area-card-name">{issue.IssueArea.issue_name}</span>
                            {canEdit && (
                                <i className="bi bi-x-circle issue-area-card-delete" onClick={() => handleDeleteUserIssue(issue.id)} />
                            )}
                        </div>
                    ))
                ) : (
                    <div className="issue-area-empty">No assigned issues</div>
                )}
            </div>
        </>
    );
}
